import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Stack, Switch, FormControlLabel,
  Alert, Divider, GridLegacy as Grid,
} from '@mui/material';
import { Email } from '@mui/icons-material';
import { emailSettingsService } from '../services/api';

interface FormState {
  enabled: boolean;
  host: string;
  port: number;
  useStartTls: boolean;
  username: string;
  password: string;
  fromAddress: string;
  fromName: string;
}

const emptyForm: FormState = {
  enabled: false,
  host: '',
  port: 587,
  useStartTls: true,
  username: '',
  password: '',
  fromAddress: 'hr@mqbittechnologies.com',
  fromName: 'Mqbit Technologies HR',
};

// Admin-only settings form for the SMTP mailbox HRMS sends from (candidate interview invites
// today, potentially other notifications later). The password field is intentionally never
// pre-filled from the server — see hasPassword/placeholder below — leaving it blank on Save
// keeps whatever password is already on file.
const EmailSettingsAdmin: React.FC = () => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [hasPassword, setHasPassword] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await emailSettingsService.get();
      setForm({
        enabled: data.enabled,
        host: data.host,
        port: data.port,
        useStartTls: data.useStartTls,
        username: data.username,
        password: '',
        fromAddress: data.fromAddress || 'hr@mqbittechnologies.com',
        fromName: data.fromName || 'Mqbit Technologies HR',
      });
      setHasPassword(data.hasPassword);
      setUpdatedAt(data.updatedAt);
    } catch {
      setError('Could not load email settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.host.trim() || !form.username.trim()) {
      setError('Host and username are required.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await emailSettingsService.update({
        enabled: form.enabled,
        host: form.host.trim(),
        port: form.port,
        useStartTls: form.useStartTls,
        username: form.username.trim(),
        password: form.password.trim() || undefined,
        fromAddress: form.fromAddress.trim(),
        fromName: form.fromName.trim(),
      });
      setSuccess('Email settings saved.');
      setForm({ ...form, password: '' });
      await load();
    } catch {
      setError('Could not save email settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Email color="primary" />
        <Typography variant="h5">Email Settings</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configures the mailbox HRMS sends candidate interview invites from. The password is stored
        encrypted in the database and is never shown back here once saved.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper sx={{ p: 3 }}>
        <FormControlLabel
          control={<Switch checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />}
          label="Enable outbound email"
        />
        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={8}>
            <TextField
              label="SMTP Host" fullWidth placeholder="smtp.gmail.com"
              value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Port" type="number" fullWidth
              value={form.port} onChange={(e) => setForm({ ...form, port: Number(e.target.value) || 587 })}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={form.useStartTls} onChange={(e) => setForm({ ...form, useStartTls: e.target.checked })} />}
              label="Use STARTTLS"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="SMTP Username" fullWidth placeholder="hr@mqbittechnologies.com"
              value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="SMTP Password" type="password" fullWidth
              placeholder={hasPassword ? 'Password already set — leave blank to keep it' : 'Enter mailbox app password'}
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="From Address" fullWidth
              value={form.fromAddress} onChange={(e) => setForm({ ...form, fromAddress: e.target.value })}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="From Name" fullWidth
              value={form.fromName} onChange={(e) => setForm({ ...form, fromName: e.target.value })}
            />
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
          <Typography variant="caption" color="text.secondary">
            {updatedAt ? `Last updated ${new Date(updatedAt).toLocaleString()}` : 'Not configured yet'}
          </Typography>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default EmailSettingsAdmin;
